import { MAX_COLORS } from '@opengov/stories-shared/constants';
import { validateHexString } from '@opengov/stories-shared/utils';
import { Check as CheckIcon } from '@opengov/react-capital-assets';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import SvgIcon from '@mui/material/SvgIcon';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import cx from 'classnames2';
import React from 'react';

import { storiesPage as messages } from '../../../../locale/stories.en.locale.json';

import styles from './ColorPicker.scss';
import { muiStyles } from './ColorPicker.styles';

export type IColorPickerProps = {
  isSettings?: boolean;
  isEditable?: boolean;
  label: string;
  colorSwatch: string[];
  activeColor?: string;
  onAddColor(color: string): any;
  onRemoveColor(color: string): any;
  onColorClick(event: React.MouseEvent<HTMLElement>, color: string): void;
};

type IColorPickerState = {
  inputVal: string;
};

export class ColorPicker extends React.Component<IColorPickerProps, IColorPickerState> {
  state = {
    inputVal: ''
  };

  onAddColor = () => {
    const { inputVal } = this.state;

    if (inputVal && validateHexString(inputVal)) {
      this.props.onAddColor(`#${inputVal}`);
      this.setState({ inputVal: '' });
    }
  };

  onColorClick = (event: React.MouseEvent<HTMLElement>, color) => {
    this.props.onColorClick(event, color);
  };

  onRemoveColor = color => {
    this.forceUpdate();
    this.props.onRemoveColor(color);
  };

  onInputChange = e => {
    this.setState({ inputVal: e.target.value });
  };

  renderColorButtons = () => {
    const { isEditable, colorSwatch, activeColor } = this.props;

    return colorSwatch.map((color: string, i) => (
      <div className={cx(styles.colorOption, 'color-swatch-item')} key={i}>
        <button
          style={{ backgroundColor: color }}
          onClick={event => this.onColorClick(event, color)}
          className={cx('colorSwatch', {
            active: color === activeColor,
            light: color === '#ffffff' || color === '#FFFFFF'
          })}
          aria-label={`Select color ${color}`}
          data-test="color-swatch"
        />
        {color === activeColor ? (
          <Box sx={muiStyles.colorBadge} data-test="selected-color-badge">
            <SvgIcon component={CheckIcon} inheritViewBox sx={muiStyles.selectedIcon} aria-hidden={true} />
          </Box>
        ) : (
          <>
            {isEditable ? (
              <Box
                role="button"
                aria-label={`Remove color ${color}`}
                className="color-remove-badge"
                sx={{ ...muiStyles.colorBadge, ...muiStyles.removeButton }}
                onClick={() => this.onRemoveColor(color)}
                data-test="color-remove"
              >
                <CloseIcon sx={muiStyles.removeIcon} aria-hidden={true} />
              </Box>
            ) : null}
          </>
        )}
      </div>
    ));
  };

  renderInputGroup() {
    const { colorSwatch } = this.props;

    return (
      <div className="color-input-group">
        <TextField
          size="small"
          value={this.state.inputVal}
          inputProps={{ maxLength: 6, 'data-test': 'color-custom', className: 'no-format', 'aria-label': messages.addColor }}
          InputProps={{
            startAdornment: <InputAdornment position="start">#</InputAdornment>
          }}
          onChange={this.onInputChange}
          sx={muiStyles.colorTextField}
        />

        <div className="color-button-wrapper">
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={this.onAddColor}
            disabled={colorSwatch.length === MAX_COLORS}
            data-test="color-add"
          >
            {messages.addColor}
          </Button>

          {colorSwatch.length === MAX_COLORS && (
            <span className="hint" data-hint={messages.maxColors} data-hint-position="top" data-hint-align="center" />
          )}
        </div>
      </div>
    );
  }

  render() {
    const { isSettings = false, isEditable = false } = this.props;

    return (
      <div
        className={cx('color-picker-container', {
          'is-settings': isSettings
        })}
      >
        <Typography variant="body2" component="span" className="comp-label">{this.props.label}</Typography>
        <div className="color-picker-wrapper" data-test="color-swatch-item">
          <div className="color-swatch-group">{this.renderColorButtons()}</div>
          {isEditable && this.renderInputGroup()}
        </div>
      </div>
    );
  }
}
